import { AppFilePicker } from './AppFilePicker';
import { Icon, type IconName } from './Icon';

export interface MenuItem {
  icon: IconName;
  label: string;
  onSelect: () => void;
  /** Опасное действие набирается цветом ошибки — как «Удалить» в карточке. */
  danger?: boolean;
  /**
   * Пункт открывает системный выбор файла, а не выполняет действие.
   *
   * Живёт здесь, а не на экране: системное окно открывается только щелчком по
   * настоящему полю, и обойти это можно лишь спрятанным полем внутри пункта.
   * Лист источников документа состоит ровно из таких пунктов.
   */
  picker?: {
    accept?: string;
    capture?: 'environment' | 'user';
    multiple?: boolean;
    onFiles: (files: File[]) => void;
  };
}

/**
 * Нижний лист с действиями.
 *
 * Один компонент на все меню приложения: «плюс», меню строки дела, меню документа.
 * На Android часть из них — `DropdownMenu` у строки, но на телефоне лист у нижнего
 * края достижим большим пальцем, а выпадающее меню у строки — нет (docs/DESIGN.md,
 * «Зона большого пальца»).
 *
 * Лист — обязательная альтернатива свайпу: любое действие, доступное жестом, должно
 * быть доступно и касанием (WCAG 2.2, SC 2.5.7).
 */
export function AppMenuSheet({
  title,
  items,
  onDismiss,
}: {
  title: string;
  items: MenuItem[];
  onDismiss: () => void;
}) {
  return (
    <div
      className="sheet-scrim"
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet__handle" />
        <p className="sheet__title">{title}</p>

        {items.map((item) =>
          item.picker ? (
            <AppFilePicker
              key={item.label}
              label={item.label}
              variant="menu"
              accept={item.picker.accept}
              capture={item.picker.capture}
              multiple={item.picker.multiple}
              onFiles={(files) => {
                item.picker?.onFiles(files);
                onDismiss();
              }}
            >
              <Icon name={item.icon} size={24} />
              <span>{item.label}</span>
            </AppFilePicker>
          ) : (
            <button
              key={item.label}
              type="button"
              className={`sheet__item ${item.danger ? 'sheet__item--danger' : ''}`}
              onClick={() => {
                item.onSelect();
                onDismiss();
              }}
            >
              <Icon name={item.icon} size={24} />
              <span>{item.label}</span>
            </button>
          ),
        )}
      </div>
    </div>
  );
}
